import type { SiteCustomizationActionAttributes } from "~/api/Api";
import type {
  ConditionGroup,
  ConditionOperator,
} from "~/components/dashboard/PageTable/AdvancedConditionsModal";

/**
 * Ransack conditions object used by both `pagesDetail` (GET, via `q`)
 * and `siteCustomizationActionsCreate` (POST, via `conditions`).
 */
export type RansackConditions = SiteCustomizationActionAttributes["conditions"];

type RansackGroup = NonNullable<RansackConditions["g"]>[string];

type BuildSearchConditionsParams = {
  selectedStatuses: string[];
  selectedPageTypes: string[];
  selectedCustomizations: string[];
  searchValue: string;
  advancedConditions?: ConditionGroup[];
  /** When true, forces status condition to ["completed"] regardless of selectedStatuses */
  forceCompleted?: boolean;
};

/** Maps a UI operator name to a ransack predicate suffix. */
const OPERATOR_TO_SUFFIX: Record<ConditionOperator, string> = {
  equals: "_eq",
  not_equals: "_not_eq",
  prefix: "_start",
  not_prefix: "_not_start",
  suffix: "_end",
  not_suffix: "_not_end",
  contains: "_cont",
  not_contains: "_not_cont",
};

/**
 * Converts a single advanced condition (field + operator + value) into
 * a key/value pair for a ransack group object.
 */
const conditionToRansack = (
  c: ConditionGroup["conditions"][number],
): Partial<RansackGroup> => {
  const key =
    `${c.field}${OPERATOR_TO_SUFFIX[c.operator]}` as keyof RansackGroup;

  return { [key]: c.value } as Partial<RansackGroup>;
};

export const buildSearchConditions = ({
  selectedStatuses,
  selectedPageTypes,
  selectedCustomizations,
  searchValue,
  advancedConditions,
  forceCompleted,
}: BuildSearchConditionsParams): RansackConditions => {
  const conditions: RansackConditions = {};

  // Status filter
  if (forceCompleted) {
    conditions.status_in = ["completed"];
  } else if (!selectedStatuses.includes("all")) {
    conditions.status_in = selectedStatuses as RansackConditions["status_in"];
  }

  // Page type filter
  if (selectedPageTypes.length > 0 && !selectedPageTypes.includes("all")) {
    conditions.kind_in = selectedPageTypes as RansackConditions["kind_in"];
  }

  // Search text filter
  if (searchValue.trim()) {
    conditions.path_cont = searchValue.trim();
  }

  // Customization kind filter
  if (selectedCustomizations.includes("none")) {
    conditions.without_customization_kind = ["title", "description", "schema"];
  } else if (
    !selectedCustomizations.includes("all") &&
    selectedCustomizations.length > 0
  ) {
    conditions.with_customization_kind =
      selectedCustomizations as RansackConditions["with_customization_kind"];
  }

  // Advanced condition groups (OR-combined).
  // Wrapped one level deeper so base filters stay AND'd at top level:
  //   q = { status_in, g: { "0": { m: "or", g: { "0": ..., "1": ... } } } }
  // Result: status_in AND (g[0][0] OR g[0][1])
  const validGroups = (advancedConditions ?? [])
    .map((g) => g.conditions.filter((c) => c.value.trim().length > 0))
    .filter((mapped) => mapped.length > 0);

  if (validGroups.length > 0) {
    const subGroups: Record<string, RansackGroup> = {};

    for (let i = 0; i < validGroups.length; i++) {
      const group: RansackGroup = { m: "and" };

      for (const c of validGroups[i]) {
        Object.assign(group, conditionToRansack(c));
      }

      subGroups[String(i)] = group;
    }

    // Wrap in a single parent group with m: "or" so top-level m stays "and"
    conditions.g = {
      "0": { m: "or", g: subGroups } as RansackGroup,
    };
  }

  return conditions;
};
